/**
 * SQL builder for schema and CRUD operations.
 */

import type {
  ColumnDefinition,
  IndexDefinition,
  WhereClause,
  OrderByClause,
  EntitySchema,
  MigrationAction,
} from './types';

export class SQLBuilder {
  static createTable<T>(schema: EntitySchema<T>, ifNotExists = true): string {
    const columns = schema.columns.map((col) => this._columnToSQL(col));
    const foreignKeys = schema.columns
      .filter((col) => col.references)
      .map((col) => this._foreignKeyToSQL(col));
    const parts = [...columns, ...foreignKeys];
    const ifNotExistsClause = ifNotExists ? 'IF NOT EXISTS ' : '';
    return `CREATE TABLE ${ifNotExistsClause}${schema.tableName} (\n  ${parts.join(',\n  ')}\n)`;
  }

  static createIndexes<T>(schema: EntitySchema<T>): string[] {
    if (!schema.indexes?.length) return [];
    return schema.indexes.map((idx) => this.createIndex(schema.tableName, idx));
  }

  static createIndex(tableName: string, index: IndexDefinition): string {
    const unique = index.unique ? 'UNIQUE ' : '';
    const where = index.where ? ` WHERE ${index.where}` : '';
    const columns = index.columns.join(', ');
    return `CREATE ${unique}INDEX IF NOT EXISTS ${index.name} ON ${tableName} (${columns})${where}`;
  }

  static dropTable(tableName: string, ifExists = true): string {
    const ifExistsClause = ifExists ? 'IF EXISTS ' : '';
    return `DROP TABLE ${ifExistsClause}${tableName}`;
  }

  static dropIndex(indexName: string, ifExists = true): string {
    const ifExistsClause = ifExists ? 'IF EXISTS ' : '';
    return `DROP INDEX ${ifExistsClause}${indexName}`;
  }

  static alterTable(tableName: string, action: MigrationAction): string {
    switch (action.type) {
      case 'addColumn':
        return `ALTER TABLE ${tableName} ADD COLUMN ${this._columnToSQL(action.column!)}`;
      case 'dropColumn':
        return `ALTER TABLE ${tableName} DROP COLUMN ${action.column!.name}`;
      case 'renameColumn':
        return `ALTER TABLE ${tableName} RENAME COLUMN ${action.oldName} TO ${action.newName}`;
      case 'addIndex':
        return this.createIndex(tableName, action.index!);
      case 'dropIndex':
        return this.dropIndex(action.index!.name);
      default:
        throw new Error(`Unknown migration action: ${(action as MigrationAction).type}`);
    }
  }

  private static _columnToSQL(col: ColumnDefinition): string {
    const parts = [col.name];
    let sqlType: string = col.type;
    if (col.type === 'JSON' || col.type === 'COMPRESSED_JSON') {
      sqlType = 'TEXT';
    } else if (col.type === 'BOOLEAN') {
      sqlType = 'INTEGER';
    }
    parts.push(sqlType);

    if (col.primary) {
      parts.push('PRIMARY KEY');
      if (col.autoIncrement) {
        parts.push('AUTOINCREMENT');
      }
    }

    if (!col.nullable && !col.primary) {
      parts.push('NOT NULL');
    }

    if (col.unique) {
      parts.push('UNIQUE');
    }

    if (col.default !== undefined) {
      const defaultValue =
        typeof col.default === 'string' ? `'${col.default.replace(/'/g, "''")}'` : String(col.default);
      parts.push(`DEFAULT ${defaultValue}`);
    }

    return parts.join(' ');
  }

  private static _foreignKeyToSQL(col: ColumnDefinition): string {
    const ref = col.references!;
    let sql = `FOREIGN KEY (${col.name}) REFERENCES ${ref.table}(${ref.column})`;
    if (ref.onDelete) sql += ` ON DELETE ${ref.onDelete}`;
    if (ref.onUpdate) sql += ` ON UPDATE ${ref.onUpdate}`;
    return sql;
  }

  static insert(
    tableName: string,
    columns: string[],
    orReplace = false,
    orIgnore = false
  ): string {
    const modifier = orReplace ? 'OR REPLACE ' : orIgnore ? 'OR IGNORE ' : '';
    const placeholders = columns.map(() => '?').join(', ');
    return `INSERT ${modifier}INTO ${tableName} (${columns.join(', ')}) VALUES (${placeholders})`;
  }

  static bulkInsert(
    tableName: string,
    columns: string[],
    rowCount: number,
    orReplace = false,
    orIgnore = false
  ): string {
    const modifier = orReplace ? 'OR REPLACE ' : orIgnore ? 'OR IGNORE ' : '';
    const rowPlaceholder = `(${columns.map(() => '?').join(', ')})`;
    const allPlaceholders = Array(rowCount)
      .fill(rowPlaceholder)
      .join(', ');
    return `INSERT ${modifier}INTO ${tableName} (${columns.join(', ')}) VALUES ${allPlaceholders}`;
  }

  static update(tableName: string, columns: string[], where: string): string {
    const setClauses = columns.map((col) => `${col} = ?`).join(', ');
    return `UPDATE ${tableName} SET ${setClauses} WHERE ${where}`;
  }

  static delete(tableName: string, where: string): string {
    return `DELETE FROM ${tableName} WHERE ${where}`;
  }

  static select(
    tableName: string,
    options: {
      columns?: string[];
      where?: string;
      orderBy?: OrderByClause;
      limit?: number;
      offset?: number;
      joins?: string[];
    } = {}
  ): string {
    const columns = options.columns?.join(', ') ?? '*';
    let sql = `SELECT ${columns} FROM ${tableName}`;
    if (options.joins?.length) {
      sql += ' ' + options.joins.join(' ');
    }
    if (options.where) {
      sql += ` WHERE ${options.where}`;
    }
    if (options.orderBy) {
      const orderClauses = Object.entries(options.orderBy)
        .map(([col, dir]) => `${col} ${dir}`)
        .join(', ');
      sql += ` ORDER BY ${orderClauses}`;
    }
    if (options.limit !== undefined) {
      sql += ` LIMIT ${options.limit}`;
    }
    if (options.offset !== undefined) {
      sql += ` OFFSET ${options.offset}`;
    }
    return sql;
  }

  static count(tableName: string, where?: string): string {
    let sql = `SELECT COUNT(*) as count FROM ${tableName}`;
    if (where) sql += ` WHERE ${where}`;
    return sql;
  }

  static buildWhere(clause: WhereClause): { sql: string; params: unknown[] } {
    if (typeof clause === 'string') {
      return { sql: clause, params: [] };
    }

    const conditions: string[] = [];
    const params: unknown[] = [];

    for (const [key, value] of Object.entries(clause)) {
      if (value === null) {
        conditions.push(`${key} IS NULL`);
      } else if (value === undefined) {
        continue;
      } else if (Array.isArray(value)) {
        const placeholders = value.map(() => '?').join(', ');
        conditions.push(`${key} IN (${placeholders})`);
        params.push(...value);
      } else if (typeof value === 'object' && value !== null) {
        for (const [op, opValue] of Object.entries(value)) {
          switch (op) {
            case '$gt':
              conditions.push(`${key} > ?`);
              params.push(opValue);
              break;
            case '$gte':
              conditions.push(`${key} >= ?`);
              params.push(opValue);
              break;
            case '$lt':
              conditions.push(`${key} < ?`);
              params.push(opValue);
              break;
            case '$lte':
              conditions.push(`${key} <= ?`);
              params.push(opValue);
              break;
            case '$ne':
              if (opValue === null) {
                conditions.push(`${key} IS NOT NULL`);
              } else {
                conditions.push(`${key} != ?`);
                params.push(opValue);
              }
              break;
            case '$like':
              conditions.push(`${key} LIKE ?`);
              params.push(opValue);
              break;
            case '$between':
              conditions.push(`${key} BETWEEN ? AND ?`);
              params.push((opValue as [unknown, unknown])[0], (opValue as [unknown, unknown])[1]);
              break;
          }
        }
      } else {
        conditions.push(`${key} = ?`);
        params.push(value);
      }
    }

    return {
      sql: conditions.length > 0 ? conditions.join(' AND ') : '1=1',
      params,
    };
  }
}
